import { CommonModule, NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation, effect, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Data, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AdvisorCrmGrpcServiceClient } from '@bp-core/src/lib/proto/advisor-crm.pbsc';
import { ConfigModel, UserModel } from '@bp-core/src/lib/proto/common-message.pb';
import { PortalUserGrpcServiceClient } from '@bp-core/src/lib/proto/portal-user.pbsc';
import { AuthService } from '@bp-core/src/lib/services/portal/auth.service';
import { UserRolesService } from '@bp-core/src/lib/services/portal/user-roles.service';
import { environment } from '@environment/environment';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { GrpcCoreModule } from '@ngx-grpc/core';
import { Empty } from '@ngx-grpc/well-known-types';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { BpNotificationBellComponent } from 'bp/layout/common/bp-notification-bell/bp-notification-bell.component';
import { BpUserComponent } from 'bp/layout/common/bp-user/bp-user.component';
import { AsesorIaLauncherComponent } from 'bp/modules/asesor-ia/components/asesor-ia-launcher.component';
import { AdvisorAiChatLoader } from 'bp/modules/asesor-ia/asesor-ia.loader';
import { usesAdvisorAiChat } from 'bp/modules/asesor-ia/asesor-ia.navigation';
import {
  CLIENT_TRANSFER_NAV_ID,
  applyClientTransferNavigationVisibility,
  canTransferClients,
} from 'bp/modules/client-transfers/client-transfer-access';
import { CRM_ACCESS_ROLES, isCrmOnlyActor } from 'bp/modules/crm/guards/crm-access.guard';
import {
  CRM_MY_TASKS_NAV_ID,
  applyMyTasksNavigationVisibility,
  canSeeMyTasks,
} from 'bp/modules/crm/guards/crm-my-tasks-access';
import {
  CONVERSION_NAV_ID,
  applyConversionNavigationVisibility,
  canSeeConversion,
} from 'bp/modules/onboarding/conversion-access';
import { BpHeaderConfig } from 'bp/shared/models/header-config';
import { BpHeaderService } from 'bp/shared/services/bp-header.service';
import {
  Subject,
  catchError,
  combineLatest,
  filter,
  map,
  of,
  startWith,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { BpVerticalNavigationComponent } from '../components/vertical/vertical.component';

/**
 * Visibilidad del menú lateral del asesor según el acceso al CRM.
 *
 * - `canAccessCrm`: el ítem CRM aparece solo con acceso efectivo confirmado por el backend.
 * - `isCrmOnlyUser` (spec 032): un usuario EXCLUSIVAMENTE de Customer Success trabaja dentro del
 *   CRM y nada más, así que el resto de las secciones de asesores desaparece del menú. El bloqueo
 *   real de esas rutas lo hace `crmOnlyRedirectGuard`; esto solo evita ofrecer lo que ya no puede
 *   abrir. Un asesor que ADEMÁS tenga rol CS conserva el menú completo (prevalece el alcance mayor).
 *
 * `chat` queda fuera del barrido: su visibilidad la resuelve el layout (depende de que el asesor
 * tenga el Asesor IA) e incorpora el mismo gate por su cuenta. `client-transfers` (feature 050) también:
 * lo resuelve `applyClientTransferNavigationVisibility` (solo jefatura). Y `onboarding` ("Conversión",
 * feature 051) lo resuelve `applyConversionNavigationVisibility` (roles de conversión, sin depender
 * del interruptor del CRM). `crm-my-tasks` ("Mis tareas", feature 054c) tampoco: lo resuelve
 * `applyMyTasksNavigationVisibility`, porque vive dentro del CRM y SÍ lo ven los solo-CS (AS-18).
 */
export function applyCrmNavigationVisibility(
  navigation: Navigation,
  canAccessCrm: () => boolean,
  isCrmOnlyUser: () => boolean = () => false,
): void {
  for (const item of navigation.bp) {
    if (item.id === 'crm') {
      item.hidden = () => !canAccessCrm();
    } else if (
      item.id !== 'chat' &&
      item.id !== CLIENT_TRANSFER_NAV_ID &&
      item.id !== CONVERSION_NAV_ID &&
      item.id !== CRM_MY_TASKS_NAV_ID
    ) {
      item.hidden = () => isCrmOnlyUser();
    }
  }
}

@Component({
  selector: 'bp-layout',
  templateUrl: './bp.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    FuseLoadingBarComponent,
    MatButtonModule,
    RouterOutlet,
    MatIconModule,
    MatTooltipModule,
    BpUserComponent,
    BpNotificationBellComponent,
    NgClass,
    GrpcCoreModule,
    BpVerticalNavigationComponent,
    AsesorIaLauncherComponent,
  ],
})
export class BpLayoutComponent implements OnInit, OnDestroy {
  /** La misma clave del portal: cada app guarda la suya en su origen. */
  private static readonly SIDEBAR_COLLAPSED_STORAGE_KEY = 'bp-sidebar-collapsed';

  private readonly _navigationService = inject(NavigationService);
  private readonly _fuseMediaWatcherService = inject(FuseMediaWatcherService);
  private readonly _fuseNavigationService = inject(FuseNavigationService);
  private readonly authService = inject(AuthService);
  private readonly portalUserGrpcServiceClient = inject(PortalUserGrpcServiceClient);
  private readonly router = inject(Router);
  private readonly activeRoute = inject(ActivatedRoute);
  private readonly bpHeaderService = inject(BpHeaderService);
  private readonly advisorCrmGrpcServiceClient = inject(AdvisorCrmGrpcServiceClient);
  private readonly userRolesService = inject(UserRolesService);
  private readonly crmAccessEnabled = signal(false);
  /** Usuario exclusivamente de Customer Success: solo trabaja dentro del CRM (spec 032). */
  private readonly crmOnlyUser = signal(false);
  /** Asesor IA (049): el menú y el botón flotante según `GetAvailability`. */
  private readonly advisorAi = inject(AdvisorAiChatLoader);

  env = environment;
  isScreenSmall: boolean;
  navigation: Navigation;
  user: UserModel;
  fileUrlBase = environment.urlApiV2 + 'api/Files/';
  config: ConfigModel;
  token = localStorage.getItem('access_token');
  headerConfig: BpHeaderConfig | null = null;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  routerUrl: string;

  /**
   * Barra lateral colapsada (riel de íconos), como en el portal (070, T048). Solo en escritorio:
   * en el celular la barra se abre encima con la hamburguesa. Queda guardada en localStorage
   * para que sobreviva a una recarga; por defecto, expandida.
   */
  readonly sidebarCollapsed = signal<boolean>(
    localStorage.getItem(BpLayoutComponent.SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true',
  );

  constructor() {
    // el ítem "Asesor IA" se muestra según el modo, que llega después del menú: se repinta al cambiar
    effect(() => {
      this.advisorAi.mode();
      if (!this.navigation?.bp?.some(item => item.id === 'chat')) return;
      this._fuseNavigationService.getComponent<BpVerticalNavigationComponent>('mainNavigation')?.refresh();
    });
    this.bpHeaderService.headerConfig$.subscribe(config => {
      setTimeout(() => {
        this.headerConfig = config;
      }, 0);
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // this.getDollarBoxIsAvalibleConfig();

    this.loadCrmAccess();

    this.routerUrl = this.router.url;
    // subscribirme al activeRoute para obtener la data de la ruta activa
    this.router.events
      // 1) Inyectamos un NavigationEnd “ficticio” usando la URL actual
      .pipe(
        startWith(new NavigationEnd(0, this.router.url, this.router.url)),
        filter(evt => evt instanceof NavigationEnd),
        map(() => {
          let route = this.activeRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        switchMap(route => route.data),
      )
      .subscribe((data: Data) => {
        // console.log('current route data:', data);
      });

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(val => {
      this.routerUrl = (val as NavigationEnd).url;
    });

    combineLatest([
      this.authService.userSubject$, // user
      this.portalUserGrpcServiceClient.getConfig(new Empty()),
    ]).subscribe(([user, config]) => {
      // console.warn([navigation, user, goals, codes, cajaMonedaConfig]);
      this.config = config;

      this._navigationService.get().subscribe(navigation => {
        if (user) this.user = user;
        const chatItem = navigation.bp.find(item => item.id === 'chat');
        const dashboardItem = navigation.bp.find(item => item.id === 'mi-vida');
        // if (!user?.isAdmin && dashboardItem) {
        //   dashboardItem.hidden = () => true;
        // }
        if (chatItem) {
          chatItem.hidden = () => !usesAdvisorAiChat(this.advisorAi.mode()) || this.crmOnlyUser();
        }
        applyCrmNavigationVisibility(navigation, this.crmAccessEnabled, this.crmOnlyUser);
        applyMyTasksNavigationVisibility(navigation, this.crmAccessEnabled, () =>
          canSeeMyTasks(this.userRolesService.roles()),
        );
        applyClientTransferNavigationVisibility(navigation, () =>
          canTransferClients(this.userRolesService.roles()),
        );
        applyConversionNavigationVisibility(
          navigation,
          () => canSeeConversion(this.userRolesService.roles()),
          this.crmOnlyUser,
        );
        this.navigation = navigation;
      });
    });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  private loadCrmAccess(): void {
    this.crmAccessEnabled.set(false);
    this.userRolesService.loadRolesFromToken();
    this.crmOnlyUser.set(isCrmOnlyActor(this.userRolesService.roles()));

    if (!this.userRolesService.hasAnyRole(...CRM_ACCESS_ROLES)) {
      return;
    }

    this.advisorCrmGrpcServiceClient
      .getCrmAccess(new Empty())
      .pipe(
        take(1),
        map(response => response.enabled),
        catchError(() => of(false)),
        takeUntil(this._unsubscribeAll),
      )
      .subscribe(enabled => this.crmAccessEnabled.set(enabled));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<BpVerticalNavigationComponent>(name);

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  /** Colapsa o expande la barra lateral y lo guarda. */
  toggleSidebarCollapsed(): void {
    const next = !this.sidebarCollapsed();
    this.sidebarCollapsed.set(next);
    localStorage.setItem(BpLayoutComponent.SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
  }

  /**
   * Con la barra colapsada, tocar un ítem con hijos (Resumen) la vuelve a expandir; el ítem queda
   * abierto por su cuenta.
   */
  onSidebarExpandRequested(): void {
    if (!this.sidebarCollapsed()) return;
    this.sidebarCollapsed.set(false);
    localStorage.setItem(BpLayoutComponent.SIDEBAR_COLLAPSED_STORAGE_KEY, 'false');
  }

  openIntercomChat($event: MouseEvent) {
    $event.preventDefault();
    try {
      (window as any).Intercom('show');
    } catch (error) {
      console.log(error);
    }
  }

  bpImgClick() {
    this.router.navigate(['/dashboard']);
  }
}
